// =============================================================================
// SHANIZ GAMING ADMIN - KYC REVIEW
// =============================================================================

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Refresh as RefreshIcon,
  Verified as VerifiedIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { kycService, KycSubmission } from '../services/api';
import { PermissionButton } from '../components/PermissionButton';
import { usePermission } from '../hooks/usePermission';

const DocumentImage: React.FC<{ label: string; src?: string }> = ({ label, src }) => {
  const [zoom, setZoom] = useState(1);
  const inc = () => setZoom((z) => Math.min(z + 0.25, 3));
  const dec = () => setZoom((z) => Math.max(z - 0.25, 0.5));
  return (
    <Box sx={{ bgcolor: '#0A0A0A', borderRadius: 2, p: 1.5, border: '1px solid #262626' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        <Box>
          <IconButton size="small" onClick={dec}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={inc}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          height: 280,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto',
          bgcolor: '#141414',
          borderRadius: 1,
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={label}
            sx={{
              transform: `scale(${zoom})`,
              transformOrigin: 'center',
              transition: 'transform 0.15s ease',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No image uploaded
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const StatusChip: React.FC<{ status: KycSubmission['status'] }> = ({ status }) => {
  const map: Record<KycSubmission['status'], { color: 'warning' | 'success' | 'error' | 'default'; label: string }> = {
    PENDING: { color: 'warning', label: 'Pending Review' },
    APPROVED: { color: 'success', label: 'Approved' },
    REJECTED: { color: 'error', label: 'Rejected' },
    NOT_SUBMITTED: { color: 'default', label: 'Not Submitted' },
  };
  const conf = map[status] || { color: 'default', label: status };
  return <Chip size="small" color={conf.color} label={conf.label} />;
};

const KycReview: React.FC = () => {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [selected, setSelected] = useState<KycSubmission | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveNote, setApproveNote] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  const canApprove = usePermission('kyc.approve').allowed;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['kyc', 'pending', page + 1, rowsPerPage],
    queryFn: () => kycService.getPending(page + 1, rowsPerPage),
  });

  const items = useMemo<KycSubmission[]>(
    () => (data?.items || (data as any)?.kyc || data?.data || (data as unknown as { submissions?: KycSubmission[] })?.submissions || []) as KycSubmission[],
    [data]
  );
  const total = data?.pagination?.total ?? data?.total ?? items.length;

  const approve = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => kycService.approve(id, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kyc'] });
      setSelected(null);
      setApproveNote('');
      setSnackbar({ open: true, msg: 'KYC approved', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Approve failed', sev: 'error' }),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => kycService.reject(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kyc'] });
      setRejectOpen(false);
      setSelected(null);
      setRejectReason('');
      setSnackbar({ open: true, msg: 'KYC rejected', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Reject failed', sev: 'error' }),
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          KYC Review
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card>
        <CardContent>
          {isError && <Alert severity="error">Failed to load KYC submissions.</Alert>}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>ID Type</TableCell>
                  <TableCell>ID Number</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" width={j === 0 ? 220 : 110} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {!isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No pending KYC submissions</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {items.map((k) => (
                  <TableRow key={k.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar src={k.user?.profilePicture || undefined} sx={{ bgcolor: 'primary.main' }}>
                          {k.user?.username?.charAt(0)?.toUpperCase() || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {k.user?.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {k.user?.email || k.userId}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{k.idType?.replace('_', ' ')}</TableCell>
                    <TableCell>{k.idNumber}</TableCell>
                    <TableCell>{dayjs(k.submittedAt).format('MMM D, YYYY HH:mm')}</TableCell>
                    <TableCell>
                      <StatusChip status={k.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Button variant="outlined" size="small" onClick={() => setSelected(k)}>
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          KYC Submission #{selected?.id?.slice(0, 8)}
          <IconButton onClick={() => setSelected(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <DocumentImage label="ID Front" src={selected.frontImageUrl} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DocumentImage label="ID Back" src={selected.backImageUrl} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* Backend sends `selfieUrl`; older UI read `selfieImageUrl`. */}
                    <DocumentImage label="Selfie" src={selected.selfieImageUrl || (selected as any).selfieUrl} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Box sx={{ bgcolor: '#141414', borderRadius: 2, p: 2, border: '1px solid #262626' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      User
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                      <Avatar src={selected.user?.profilePicture || undefined}>
                        {selected.user?.username?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={600}>{selected.user?.username}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selected.user?.email || selected.userId}
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">ID Type</Typography>
                        <Typography variant="body2">{selected.idType?.replace('_', ' ')}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">ID Number</Typography>
                        <Typography variant="body2">{selected.idNumber}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Full Name</Typography>
                        <Typography variant="body2">{selected.metadata?.fullName || '—'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">DOB</Typography>
                        <Typography variant="body2">{selected.metadata?.dateOfBirth || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Address</Typography>
                        <Typography variant="body2">{selected.metadata?.address || '—'}</Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ bgcolor: '#141414', borderRadius: 2, p: 2, border: '1px solid #262626' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Status Timeline
                    </Typography>
                    <Stepper orientation="vertical" activeStep={(selected.history?.length ?? 1) - 1} sx={{ '.MuiStepLabel-label': { color: '#F5F5F5' } }}>
                      {(selected.history && selected.history.length
                        ? selected.history
                        : [{ at: selected.submittedAt, status: 'PENDING' as const, note: 'Submitted' }]
                      ).map((h, i) => (
                        <Step key={i} expanded>
                          <StepLabel>
                            <StatusChip status={h.status} />
                            <Typography variant="caption" sx={{ ml: 1 }} color="text.secondary">
                              {dayjs(h.at).format('MMM D, YYYY HH:mm')}
                            </Typography>
                          </StepLabel>
                          <StepContent>
                            <Typography variant="body2" color="text.secondary">
                              {h.note || (h.by ? `By ${h.by}` : 'No note')}
                            </Typography>
                          </StepContent>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  <TextField
                    label="Approval note (optional)"
                    multiline
                    rows={2}
                    value={approveNote}
                    onChange={(e) => setApproveNote(e.target.value)}
                  />
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          <PermissionButton
            permission="kyc.reject"
            color="error"
            variant="outlined"
            startIcon={<BlockIcon />}
            onClick={() => setRejectOpen(true)}
            disabled={!selected || reject.isPending}
          >
            Reject
          </PermissionButton>
          <PermissionButton
            permission="kyc.approve"
            color="success"
            variant="contained"
            startIcon={<VerifiedIcon />}
            disabled={!selected || approve.isPending || !canApprove}
            onClick={() => selected && approve.mutate({ id: selected.id, note: approveNote || undefined })}
          >
            Approve
          </PermissionButton>
        </DialogActions>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject KYC Submission</DialogTitle>
        <DialogContent>
          <Typography gutterBottom color="text.secondary">
            The user will be notified. Be specific so they know what to correct.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || reject.isPending || !selected}
            onClick={() => selected && reject.mutate({ id: selected.id, reason: rejectReason.trim() })}
          >
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KycReview;
